import { useEffect, useState } from 'react';

type UseSearchProps<T> = {
  data: T[];
  searchFunction: (searchTerm: string) => T[];
  onClear?: () => void;
};

export const useSearch = <T>({ data, searchFunction, onClear }: UseSearchProps<T>) => {
  const [filteredData, setFilteredData] = useState<T[]>(data);
  const [selectedElement, setSelectedElement] = useState<T | undefined>(undefined);

  useEffect(() => {
    // avoid re-rendering when the data is the same
    if (JSON.stringify(filteredData) !== JSON.stringify(data)) {
      setFilteredData(data);
    }
  }, [data]);

  const resetState = () => {
    setFilteredData(data);
    setSelectedElement(undefined);
  };

  const onSearch = (searchTerm: string) => {
    if (!searchTerm || searchTerm === '') {
      resetState();
      onClear?.();
      return;
    }
    const searchResults = searchFunction(searchTerm);
    setFilteredData(searchResults);
    setSelectedElement(searchResults[0]);
  };

  const onEnter = (e: string) => {
    if (e === 'Enter' && selectedElement) {
      const indexOfSelectedElement = filteredData.indexOf(selectedElement);
      if (indexOfSelectedElement === filteredData.length - 1) {
        setSelectedElement(filteredData[0]);
        return;
      }
      setSelectedElement(filteredData[indexOfSelectedElement + 1]);
    }
  };

  const onSelect = (key: number | undefined) => {
    if (key === undefined) {
      return setSelectedElement(undefined);
    }

    setSelectedElement(filteredData[key]);
  };

  return { onSearch, onSelect, onEnter, filteredData, selectedElement };
};
